"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import Image from "next/image";
import dynamic from "next/dynamic";
import { Link } from "@/i18n/navigation";
import { eventLinkRel } from "@/lib/seo-enrichment";
import { MapPin, ExternalLink } from "lucide-react";
import { Event } from "@/lib/types";
import { CITIES, getCitySlugForVenueCity } from "@/lib/constants";
import {
  EventGroup,
  formatDate,
  formatDateRange,
  formatTime,
  formatPrice,
  getEventImage,
  getEventGenres,
} from "@/lib/utils";
import { useSpotifyPlayer } from "../spotify/spotify-player-context";
import { useAuth } from "../auth/auth-context";
import { SubscribeButton } from "../subscribe-button";
import { EventGenreChip } from "./event-genre-chip";
import { useGenreTagLinkMode } from "./feed-genre-filter-context";
import { PlayIcon } from "@/components/spotify/play-icon";

const EventShowingsModal = dynamic(() => import("./event-showings-modal").then((module) => module.EventShowingsModal), { ssr: false });
const EventAdminModal = dynamic(() => import("../admin/event-admin-modal").then((module) => module.EventAdminModal), { ssr: false });

interface EventCardProps {
  event: Event;
  eventGroup?: EventGroup;
  blockedSpotifyIds: string[];
  citySlug?: string;
  preferredArtistId?: number;
  showCity?: boolean;
}

const GENRE_INITIAL_COUNT = 3;

export function EventCard({ event, eventGroup, blockedSpotifyIds, citySlug, showCity }: EventCardProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [genresExpanded, setGenresExpanded] = useState(false);
  const t = useTranslations("Events");
  const genreTagLinkMode = useGenreTagLinkMode();
  const tCommon = useTranslations("Common");
  const tFilters = useTranslations("Filters");
  const locale = useLocale();
  const { isAdmin } = useAuth();
  const imageUrl = getEventImage(event);
  const allGenres = getEventGenres(event);
  const showCount = eventGroup ? (eventGroup.totalShowCount ?? eventGroup.events.length) : 1;
  const isGroup = eventGroup && showCount > 1;

  // Date display
  const dateDisplay = isGroup
    ? formatDateRange(
        eventGroup.events[0].local_date,
        eventGroup.events[eventGroup.events.length - 1].local_date,
        locale,
        t("dateTba")
      )
    : formatDate(event.local_date, locale, t("dateTba"));
  const time = isGroup ? null : formatTime(event.local_time, locale);

  // Price: for groups, show range across all events
  const price = isGroup
    ? (() => {
        const mins = eventGroup.events
          .map((e) => e.price_min)
          .filter((v): v is number => v != null);
        const maxes = eventGroup.events
          .map((e) => e.price_max)
          .filter((v): v is number => v != null);
        const groupMin = mins.length > 0 ? Math.min(...mins) : null;
        const groupMax = maxes.length > 0 ? Math.max(...maxes) : null;
        return formatPrice(groupMin, groupMax, event.price_currency, locale, t("priceTba"));
      })()
    : formatPrice(event.price_min, event.price_max, event.price_currency, locale, t("priceTba"));

  const { play, artistId: activeArtistId } = useSpotifyPlayer();

  // Find first attraction with a playable spotify_id
  const spotifyAttraction = event.attractions.find(
    (a) => a.spotify_id && !blockedSpotifyIds.includes(a.spotify_id)
  );
  const isPlaying = spotifyAttraction?.spotify_id === activeArtistId;

  // Derive city slug from venue city if not provided via prop
  const resolvedCitySlug = event.venue.city_slug ?? citySlug ?? getCitySlugForVenueCity(event.venue.city ?? "");

  // Event page URL (internal) or fallback to Ticketmaster
  const eventHref = resolvedCitySlug
    ? `/${resolvedCitySlug}/event/${event.slug}`
    : event.url || "#";
  const isInternal = !!resolvedCitySlug;

  return (
    <>
      {/* Outer card container with relative positioning for the overlay link */}
      <div className="group relative overflow-hidden rounded-lg border border-neutral-800 bg-surface transition-all duration-300 hover:border-acid/30 hover:shadow-[0_0_30px_rgba(209,248,5,0.05)]">
        {/* Zone 1: Full-card link overlay — z-[1] sits above image but below interactive z-10 elements */}
        {isInternal ? (
          <Link
            href={eventHref}
            rel={eventLinkRel(event)} prefetch={false}
            className="absolute inset-0 z-[1]"
            aria-label={`View ${event.name} details`}
          />
        ) : (
          <a
            href={eventHref}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute inset-0 z-[1]"
            aria-label={`View ${event.name} on Ticketmaster`}
          />
        )}

        {/* Image */}
        <div className="relative aspect-video overflow-hidden bg-neutral-900">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={event.name}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              style={{ objectPosition: event.image_focus || "center" }}
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <span className="font-mono text-xs text-neutral-700">
                {tCommon("noImage")}
              </span>
            </div>
          )}

          {/* Admin edit button */}
          {isAdmin && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setAdminModalOpen(true);
              }}
              className={`absolute right-2 z-10 flex h-7 w-7 items-center justify-center rounded bg-black/70 text-neutral-400 pointer-events-auto opacity-0 transition-all group-hover:opacity-100 hover:text-acid hover:bg-black/90 ${isGroup ? "top-12" : "top-2"}`}
              title="Admin edit"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </button>
          )}

          {/* Zone 3: Show count badge for groups — opens modal */}
          {isGroup && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setModalOpen(true);
              }}
              className="absolute top-3 right-3 z-10 rounded bg-black/80 px-2 py-1 font-mono text-[10px] font-bold text-acid backdrop-blur-sm pointer-events-auto hover:bg-acid hover:text-black transition-colors"
            >
              {t("showsBadge", { count: showCount })}
            </button>
          )}

          {/* Spotify play button */}
          {spotifyAttraction && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                play(spotifyAttraction.spotify_id!, spotifyAttraction.name, { event, artistId: spotifyAttraction.id });
              }}
              className={`spotify-btn absolute bottom-3 right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full pointer-events-auto transition-all ${
                isPlaying
                  ? "bg-[#1DB954] text-black shadow-[0_0_12px_rgba(29,185,84,0.5)]"
                  : "bg-black/70 text-white hover:bg-[#1DB954] hover:text-black"
              }`}
              title={`Preview ${spotifyAttraction.name} on Spotify`}
            >
              <PlayIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-4 pb-4 pt-2">
          {/* Artist name */}
          <div className="mb-1 flex items-center gap-2">
            <h3 className="min-w-0 flex-1 text-sm font-semibold leading-tight text-white line-clamp-1">
              {event.attractions.length > 0
                ? event.attractions.map((a) => a.name).join(" / ")
                : event.name}
            </h3>
            <div className="relative z-10 pointer-events-auto -mr-1">
              <SubscribeButton
                type="artist"
                targetId={event.attractions[0]?.id ?? 0}
                targetName={event.attractions[0]?.name ?? event.name}
                targetImage={imageUrl}
                size="sm"
                event={event}
              />
            </div>
          </div>

          {/* Event name (if different from artist) */}
          {event.attractions.length > 0 &&
            event.name !== event.attractions.map((a) => a.name).join(" / ") && (
              <p className="mb-2 text-xs text-neutral-400 line-clamp-1">
                {event.name}
              </p>
            )}

          {/* Zone 3: Date & time — for groups, clickable to open modal */}
          {isGroup ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setModalOpen(true);
              }}
              className="relative z-10 pointer-events-auto mb-2 font-mono text-xs text-neutral-300 hover:text-acid transition-colors"
            >
              {dateDisplay}
            </button>
          ) : (
            <p className="mb-2 font-mono text-xs text-neutral-300">
              {dateDisplay}
              {time && (
                <span className="text-neutral-500"> · {time}</span>
              )}
            </p>
          )}

          {/* Venue */}
          <div className="mb-3 flex items-center gap-1.5">
            <MapPin className="h-3 w-3 shrink-0 text-neutral-500" />
            {resolvedCitySlug ? (
              <Link
                href={`/${resolvedCitySlug}/${event.venue.slug}`}
                prefetch={false}
                onClick={(e) => e.stopPropagation()}
                className="relative z-10 pointer-events-auto text-xs text-neutral-400 line-clamp-1 hover:text-acid transition-colors"
              >
                {event.venue.name}
              </Link>
            ) : (
              <span className="text-xs text-neutral-400 line-clamp-1">
                {event.venue.name}
              </span>
            )}
            {showCity && resolvedCitySlug && CITIES[resolvedCitySlug] && (
              <>
                <span className="text-xs text-neutral-600">·</span>
                <Link
                  href={`/${resolvedCitySlug}`}
                  onClick={(e) => e.stopPropagation()}
                  className="relative z-10 pointer-events-auto shrink-0 text-xs text-neutral-500 hover:text-acid transition-colors"
                >
                  {CITIES[resolvedCitySlug].name}
                </Link>
              </>
            )}
          </div>

          {/* Bottom row: genres + price */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1">
              {(genresExpanded ? allGenres : allGenres.slice(0, GENRE_INITIAL_COUNT)).map((genre) => (
                genreTagLinkMode === "directory" ? <EventGenreChip key={genre} genre={genre} citySlug={citySlug} pill /> : <span
                  key={genre}
                  className="rounded-full border border-acid/30 px-2 py-0.5 font-mono text-[10px] text-acid"
                >
                  {genre.toUpperCase()}
                </span>
              ))}
              {allGenres.length > GENRE_INITIAL_COUNT && (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setGenresExpanded(!genresExpanded); }}
                  className="rounded-full border border-neutral-700 px-2 py-0.5 font-mono text-[10px] text-neutral-400 hover:text-neutral-300"
                >
                  {genresExpanded ? tCommon("less") : tFilters("moreGenres", { count: allGenres.length - GENRE_INITIAL_COUNT })}
                </button>
              )}
            </div>
            {/* Zone 2: Price / Tickets link */}
            {(event.url || event.venue.url) ? (
              <a
                href={(event.url || event.venue.url) as string}
                target="_blank"
                rel="noopener noreferrer"
                data-ticket-link=""
                data-event-id={event.id}
                onClick={(e) => e.stopPropagation()}
                className="relative z-10 pointer-events-auto flex items-center gap-1 font-mono text-xs text-neutral-400 hover:text-acid transition-colors"
              >
                {price}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : event.price_min != null ? (
              <span className="font-mono text-xs text-neutral-400">{price}</span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Modal for grouped events */}
      {modalOpen && isGroup && (
        <EventShowingsModal
          eventGroup={eventGroup}
          onClose={() => setModalOpen(false)}
          citySlug={citySlug}
          blockedSpotifyIds={blockedSpotifyIds}
        />
      )}

      {/* Admin edit modal */}
      {adminModalOpen && (
        <EventAdminModal
          event={event}
          onClose={() => setAdminModalOpen(false)}
        />
      )}
    </>
  );
}
